import { useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Command as CommandPrimitive } from "cmdk";
import { CornerDownLeft, SearchX, Search } from "lucide-react";
import { MenuItem } from "@/types/domain";
import { MenuIcon } from "./MenuIcon";
import { useMenuNavigation } from "./useMenuNavigation";

type JumpTarget = {
  item: MenuItem;
  group: string;
  category: string | null;
}

const NAVIGABLE_TYPES = ["link", "popup", "pipelines"];
const PAGES_GROUP = "Pages";

const collectDropdownTargets = (dropdown: MenuItem, group: string, targets: JumpTarget[]) => {
  for (const child of dropdown.children ?? []) {
    if (!child.isVisible) continue;
    if (child.type === "dropdown") collectDropdownTargets(child, group, targets);
    else if (NAVIGABLE_TYPES.includes(child.type) && child.url) targets.push({ item: child, group, category: dropdown.label });
  }
}

export const buildJumpTargets = (menuItems: MenuItem[]): JumpTarget[] => {
  const targets: JumpTarget[] = [];
  for (const item of menuItems) {
    if (!item.isVisible) continue;
    if (item.type === "settings") {
      for (const category of item.children ?? []) {
        for (const leaf of category.children ?? []) {
          if (leaf.isVisible && NAVIGABLE_TYPES.includes(leaf.type)) targets.push({ item: leaf, group: `Settings: ${category.label}`, category: category.label });
        }
      }
    } else if (item.type === "dropdown") {
      collectDropdownTargets(item, item.label, targets);
    } else if (NAVIGABLE_TYPES.includes(item.type) && item.url) {
      targets.push({ item, group: item.section || PAGES_GROUP, category: item.section ?? null });
    }
  }
  return targets;
}

const groupTargets = (targets: JumpTarget[]) => {
  const groups = new Map<string, JumpTarget[]>();
  for (const target of targets) {
    groups.set(target.group, [...(groups.get(target.group) ?? []), target]);
  }
  return Array.from(groups.entries());
}

const Kbd = ({ children }: { children: React.ReactNode }) => (
  <kbd className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-sans text-[11px] leading-normal font-normal text-slate-500 shadow-none">{children}</kbd>
);

const FooterKbd = ({ children }: { children: React.ReactNode }) => (
  <kbd className="mr-1 inline-block min-w-5 rounded border border-slate-200 bg-white px-[5px] py-px text-center font-sans text-[11px] font-normal leading-normal text-slate-600 shadow-none">{children}</kbd>
);

const itemClass =
  "flex cursor-pointer select-none items-center gap-3 rounded-lg px-2.5 py-[9px] text-sm text-slate-800 outline-none " +
  "data-[selected=true]:bg-(--layout-side-nav-active-bg) data-[selected=true]:text-white";

const groupClass =
  "p-0 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2.5 [&_[cmdk-group-heading]]:text-[11px] " +
  "[&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.06em] [&_[cmdk-group-heading]]:text-slate-500";

export const scoreJumpMatch = (value: string, search: string, keywords: string[] = []): number => {
  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return 1;
  const primary = value.toLowerCase();
  const all = `${primary} ${keywords.join(" ").toLowerCase()}`;
  if (!terms.every((t) => all.includes(t))) return 0;
  return terms.every((t) => primary.includes(t)) ? 1 : 0.5;
}

type JumpToPaletteProps = {
  menuItems: MenuItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const JumpToPalette = ({ menuItems, open, onOpenChange }: JumpToPaletteProps) => {
  const { activate } = useMenuNavigation();
  const groups = useMemo(() => groupTargets(buildJumpTargets(menuItems)), [menuItems]);
  const openerRef = useRef<HTMLElement | null>(null);
  const [query, setQuery] = useState("");

  const handleOpenChange = (next: boolean) => {
    if (!next) setQuery("");
    onOpenChange(next);
  }

  const handleSelect = (item: MenuItem) => {
    handleOpenChange(false);
    activate(item);
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-[3px]" />
        <DialogPrimitive.Content
          data-testid="jump-to-palette"
          aria-describedby="jump-to-description"
          onOpenAutoFocus={() => { openerRef.current = document.activeElement as HTMLElement | null; }}
          onCloseAutoFocus={(event) => { event.preventDefault(); openerRef.current?.focus(); }}
          className="fixed left-1/2 top-[14vh] z-50 w-[640px] max-w-[calc(100vw-32px)] -translate-x-1/2 overflow-hidden rounded-xl bg-white text-sm text-slate-800 shadow-[0_24px_64px_rgba(15,23,42,0.35),0_0_0_1px_rgba(15,23,42,0.06)] outline-none"
        >
          <DialogPrimitive.Title className="sr-only">Jump to</DialogPrimitive.Title>
          <DialogPrimitive.Description id="jump-to-description" className="sr-only">Search for a page or setting</DialogPrimitive.Description>
          <CommandPrimitive loop filter={scoreJumpMatch} className="flex w-full flex-col">
            <div className="flex items-center gap-3 border-b border-slate-200 px-4" cmdk-input-wrapper="">
                          <Search className="size-4 shrink-0 text-slate-400" aria-hidden />
              <CommandPrimitive.Input
                value={query}
                onValueChange={setQuery}
                placeholder="Search pages and settings..."
                className="h-14 w-full rounded-none! border-0! bg-transparent! p-0! text-base text-slate-800 shadow-none! outline-none! ring-0! placeholder:text-slate-400"
              />
              <Kbd>Esc</Kbd>
            </div>
            <CommandPrimitive.List className="            max-h-[min(380px,60vh)] overflow-y-auto overflow-x-hidden overscroll-contain p-2">
                          <CommandPrimitive.Empty className="flex flex-col items-center gap-1 px-2.5 py-8 text-center text-sm text-slate-500">
                            <SearchX className="size-6 text-slate-300" aria-hidden />
                            <span className="font-semibold text-slate-800">No results found</span>
                            <span className="text-xs">Try a different search term.</span>
                          </CommandPrimitive.Empty>
              {groups.map(([group, targets]) => (
                <CommandPrimitive.Group key={group} heading={group} className={groupClass}>
                  {targets.map(({ item, category }) => (
                    <CommandPrimitive.Item
                      key={item.id}
                      value={`${item.label} ${item.hint ?? ""} ${category ?? ""} ${group}`}
                      keywords={item.keywords ? item.keywords.split(/\s+/) : undefined}
                      onSelect={() => handleSelect(item)}
                      className={itemClass}
                    >
                      {item.icon
                                              ? <MenuIcon name={item.icon} className="w-[18px] shrink-0 text-slate-500 [[data-selected=true]_&]:text-white/85" />
                                              : <span className="w-[18px] shrink-0" aria-hidden />}
                                            <span className="flex-1 truncate">{item.label}</span>
                                            {item.hint && (
                                              <span className="shrink-0 text-xs text-slate-400 [[data-selected=true]_&]:text-white/85">{item.hint}</span>
                                            )}
                    </CommandPrimitive.Item>
                  ))}
                </CommandPrimitive.Group>
              ))}
            </CommandPrimitive.List>
            <div className="flex items-center gap-4 border-t border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-500 [&_span]:text-slate-500" aria-hidden>
                          <span className="flex items-center"><FooterKbd>↑</FooterKbd><FooterKbd>↓</FooterKbd> Navigate</span>
                          <span className="flex items-center"><FooterKbd><CornerDownLeft className="inline size-3" /></FooterKbd> Open</span>
                          <span className="flex items-center"><FooterKbd>Esc</FooterKbd> Close</span>
            </div>
          </CommandPrimitive>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
